// Het vlak Bronnen & dekking (W6 ronde 3, besluit V1; research §3.4, besluit D2).
//
// Drie vragen, drie tabbladen:
//  · Het corpus  — hoe ziet wat we lazen eruit? (GET /api/beheer/bronnen)
//  · Dekking     — waar zitten de gaten, en wat doen we eraan? (…/dekking)
//  · Bronnenlijst — welke uitgevers wegen hoe, en wat zijn ze waard? (…/bronnen/lijsten)
//
// Deze schil draagt de sectorkeuze en de drie ophaalrondes. Dekking en
// Bronnenlijst halen pas op als hun tabblad open staat: wie alleen het corpus
// komt bekijken, laat de database niet voor niets rekenen. Het corpus haalt
// altijd op, want zijn antwoord draagt ook de lijst met sectoren.
//
// Een antwoord van een andere sector tonen we nooit onder de gekozen sector:
// na een wissel staat er "wordt opgehaald" tot het nieuwe antwoord er is, in
// plaats van de getallen van de vorige sector (W8-ontwerpregel 1).

import { useEffect, useId, useState, type ReactNode } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useStartSector } from "../../lib/sectorkeuze";
import { useResource, type Resource } from "../../lib/useResource";
import type { BronLijstenResponse, BronnenResponse, DekkingResponse } from "../../contracts";
import { color, radius, size } from "../../tokens";
import { BronnenCorpus } from "./BronnenCorpus";
import { BronnenDekking } from "./BronnenDekking";
import { BronnenLijst } from "./BronnenLijst";
import { invoer } from "./stijl";

/** De ascii-sleutel reist mee naar het gebruikslog ("Beheer/Bronnen/Dekking");
 *  het label is wat de beheerder leest. */
type TabKey = "Corpus" | "Dekking" | "Lijst";

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: "Corpus", label: "Het corpus" },
  { key: "Dekking", label: "Dekking" },
  { key: "Lijst", label: "Bronnenlijst" },
];

export function Bronnen() {
  // Pilot gereed 26-10 (1C): het vlak opent op de sector waar de schermen op
  // staan (de eerste zichtbare voor beheer), niet vast op ICT. ICT blijft in de
  // keuzelijst voor beheer.
  const [sector, setSector] = useState(useStartSector());
  const [tab, setTab] = useState<TabKey>("Corpus");
  const sectorId = useId();

  const bronnen = useResource<BronnenResponse>(() => api.bronnen(sector), [sector]);
  const dekking = useResource<DekkingResponse>(
    () => api.dekking(sector),
    [sector],
    tab === "Dekking",
  );
  const lijsten = useResource<BronLijstenResponse>(
    () => api.bronLijsten(sector),
    [sector],
    tab === "Lijst",
  );

  // Welk tabblad, zoals de schil het vlak meldt (W6, research §4): alleen WAAR
  // iemand kijkt, nooit wat erin staat. Ook het eerste tabblad telt.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  useEffect(() => {
    void api.gebruik({ scherm: `Beheer/Bronnen/${tab}` }).catch(() => {});
  }, [tab]);

  const corpus = vanSector(bronnen.data, sector);
  const dek = vanSector(dekking.data, sector);
  const lijst = vanSector(lijsten.data, sector);
  // De sectorlijst is voor elke sector dezelfde; die mag van het vorige
  // antwoord blijven staan, zodat de keuze niet verdwijnt tijdens het laden.
  const sectoren = bronnen.data?.sectoren ?? null;
  const gekozen = sectoren?.find((s) => s.key === sector) ?? null;

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 18,
        }}
      >
        {/* Hetzelfde gesegmenteerde patroon als de subnavigatie van Beheer. */}
        <nav
          aria-label="Onderdelen van Bronnen & dekking"
          style={{
            display: "inline-flex",
            flexWrap: "wrap",
            gap: 4,
            background: color.lineFaint,
            borderRadius: radius.input,
            padding: 3,
          }}
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={"view-btn" + (tab === t.key ? " active" : "")}
              aria-pressed={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {sectoren && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <label htmlFor={sectorId} style={{ fontSize: size.meta, color: color.soft }}>
              Sector
            </label>
            {/* Een keuzelijst, geen schuif (owner-ontwerptaal). Een sector die
                nog niet aan staat blijft kiesbaar — zijn bronnenlijst kun je al
                bekijken — maar staat grijs, met de reden in het woord. */}
            <select
              id={sectorId}
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              style={{ ...invoer, width: "auto", minWidth: 220, padding: "7px 10px" }}
            >
              {sectoren.map((s) => (
                <option
                  key={s.key}
                  value={s.key}
                  style={s.actief ? undefined : { color: color.faint }}
                >
                  {s.actief ? s.label : `${s.label} · nog geen corpus`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {gekozen && !gekozen.actief && (
        <p role="note" style={{ fontSize: size.meta, color: color.accentAmber, marginBottom: 14 }}>
          {`${gekozen.label} staat nog niet aan — nog geen corpus.`}
        </p>
      )}

      {tab === "Corpus" && (
        <Wacht bron={bronnen} data={corpus} wat="het corpus">
          {(d) => <BronnenCorpus statistiek={d.statistiek} />}
        </Wacht>
      )}
      {tab === "Dekking" && (
        <Wacht bron={dekking} data={dek} wat="de dekking">
          {/* Per sector een eigen staat: "staat op de wachtrij" hoort bij
              het gat van déze sector, niet bij dezelfde categorie elders. */}
          {(d) => <BronnenDekking key={sector} data={d} sector={sector} />}
        </Wacht>
      )}
      {tab === "Lijst" && (
        <Wacht bron={lijsten} data={lijst} wat="de bronnenlijst">
          {(d) => (
            <BronnenLijst
              key={sector}
              data={d}
              sector={sector}
              bronsoorten={corpus ? corpus.statistiek.bronsoort : null}
              onGewijzigd={lijsten.reload}
            />
          )}
        </Wacht>
      )}
    </div>
  );
}

/** Het antwoord alleen als het over de gekozen sector gaat. */
function vanSector<T extends { sector: string }>(data: T | null, sector: string): T | null {
  return data && data.sector === sector ? data : null;
}

/** Laden, mislukt of klaar — met een reden in de eerste twee gevallen. Tijdens
 *  een herlaadronde (na een besluit) blijft de inhoud staan en dimt hij licht. */
function Wacht<T>({
  bron,
  data,
  wat,
  children,
}: {
  bron: Resource<unknown>;
  data: T | null;
  wat: string;
  children: (data: T) => ReactNode;
}) {
  if (bron.error) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={bron.error} onRetry={bron.reload} />
      </Card>
    );
  }
  if (data === null) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason={`${wat} wordt opgehaald`} />
      </Card>
    );
  }
  return (
    <div style={{ opacity: bron.stale ? 0.6 : 1, transition: "opacity 0.15s" }}>
      {children(data)}
    </div>
  );
}
